import { useState, useEffect } from "react";
import { ShieldCheck, ShieldAlert, ShieldQuestion, AlertTriangle, ChevronDown, ChevronUp, ExternalLink, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const RecallDetail = ({ recall, index }) => (
  <div className={`py-3.5 ${index > 0 ? "border-t border-slate-100" : ""}`}>
    <div className="flex items-start justify-between gap-3 mb-1.5">
      <span className="text-sm font-medium text-slate-900">{recall.component}</span>
      <span className="text-xs font-mono text-slate-400 flex-shrink-0">{recall.campaignNumber}</span>
    </div>
    <p className="text-sm text-slate-600 leading-relaxed">{recall.summary}</p>
    {recall.remedy && (
      <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
        <span className="font-medium text-slate-600">Remedy:</span> {recall.remedy}
      </p>
    )}
  </div>
);

const VehicleRecallTile = ({ vin, vehicleInfo, offerId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Stabilize vehicleInfo to prevent infinite re-renders from object reference changes
  const viYear = vehicleInfo?.year;
  const viMake = vehicleInfo?.make;
  const viModel = vehicleInfo?.model;
  const viTrim = vehicleInfo?.trim;

  useEffect(() => {
    if (!vin) return;
    let cancelled = false;
    const fetchRecalls = async () => {
      try {
        const payload = {
          vin,
          offerId: offerId || undefined,
          context: "nucarnext-offer-page",
        };
        if (viMake && viModel && viYear) {
          payload.vehicle = {
            year: parseInt(viYear) || undefined,
            make: viMake || undefined,
            model: viModel || undefined,
            trim: viTrim || undefined,
          };
        }
        const res = await axios.post(`${API}/vehicle-recall-check`, payload);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) {
          setData({
            status: "unavailable",
            headline: "Recall check temporarily unavailable",
            subtext: "You can still continue with your offer.",
            recalls: [],
            openRecallCount: 0,
            fallbackUrl: "https://www.nhtsa.gov/recalls",
            disclaimer: "Open safety recalls only.",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRecalls();
    return () => { cancelled = true; };
  }, [vin, viYear, viMake, viModel, viTrim, offerId]);

  // ===== LOADING STATE =====
  if (loading) {
    return (
      <div className="surface-card p-6" data-testid="recall-tile-loading">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
          </div>
          <div className="flex-1">
            <div className="h-4 w-44 bg-slate-100 rounded animate-pulse mb-2" />
            <div className="h-3 w-60 bg-slate-50 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { status, headline, subtext, recalls, openRecallCount, fallbackUrl, disclaimer, checkedAt } = data;

  // Time formatting
  const timeLabel = checkedAt
    ? (() => {
        const diff = Date.now() - new Date(checkedAt).getTime();
        if (diff < 60000) return "Last checked just now";
        if (diff < 3600000) return `Last checked ${Math.floor(diff / 60000)}m ago`;
        return `Checked ${new Date(checkedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
      })()
    : null;

  // ===== CLEAR STATE =====
  if (status === "clear") {
    return (
      <div className="surface-card overflow-hidden" data-testid="recall-tile-clear">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900">Vehicle Recall Check</h3>
              <p className="text-sm text-emerald-700 font-medium mt-1">{headline}</p>
              <p className="text-sm text-slate-500 mt-0.5">{subtext}</p>
              {timeLabel && (
                <p className="text-xs text-slate-400 mt-2">{timeLabel}</p>
              )}
            </div>
          </div>
        </div>
        {disclaimer && (
          <div className="px-6 py-2.5 bg-slate-50 border-t border-slate-100">
            <p className="text-xs text-slate-400 leading-relaxed">{disclaimer}</p>
          </div>
        )}
      </div>
    );
  }

  // ===== OPEN RECALL STATE =====
  if (status === "open_recall") {
    return (
      <div className="surface-card overflow-hidden" data-testid="recall-tile-open">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "hsl(48, 96%, 93%)" }}>
              <ShieldAlert className="w-5 h-5" style={{ color: "hsl(45, 90%, 35%)" }} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900">Vehicle Recall Check</h3>
              <p className="text-sm font-medium mt-1" style={{ color: "hsl(45, 90%, 30%)" }}>
                {headline}
              </p>
              <p className="text-sm text-slate-500 mt-0.5">{subtext}</p>
              {timeLabel && (
                <p className="text-xs text-slate-400 mt-2">{timeLabel}</p>
              )}
            </div>
          </div>
        </div>

        {/* Expandable recall details */}
        {recalls && recalls.length > 0 && (
          <div className="border-t border-slate-100">
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-between px-6 py-3 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="recall-expand-btn"
            >
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" style={{ color: "hsl(45, 90%, 35%)" }} />
                <span className="font-medium">
                  View {openRecallCount} Recall Detail{openRecallCount > 1 ? "s" : ""}
                </span>
              </span>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {expanded && (
              <div className="px-6 pb-4" data-testid="recall-details-expanded">
                {recalls.map((recall, i) => (
                  <RecallDetail key={recall.campaignNumber || i} recall={recall} index={i} />
                ))}
              </div>
            )}
          </div>
        )}

        {disclaimer && (
          <div className="px-6 py-2.5 bg-slate-50 border-t border-slate-100">
            <p className="text-xs text-slate-400 leading-relaxed">{disclaimer}</p>
          </div>
        )}
      </div>
    );
  }

  // ===== UNAVAILABLE / UNSUPPORTED STATES =====
  const isUnsupported = status === "unsupported";
  return (
    <div className="surface-card overflow-hidden" data-testid={`recall-tile-${status}`}>
      <div className="p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
            <ShieldQuestion className="w-5 h-5 text-slate-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">Vehicle Recall Check</h3>
            <p className="text-sm text-slate-600 mt-1">{headline}</p>
            <p className="text-sm text-slate-500 mt-0.5">{subtext}</p>
            {fallbackUrl && (
              <a
                href={fallbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 mt-3 font-medium transition-colors"
                data-testid="recall-fallback-link"
              >
                Check official recall source
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>
      </div>
      {disclaimer && (
        <div className="px-6 py-2.5 bg-slate-50 border-t border-slate-100">
          <p className="text-xs text-slate-400 leading-relaxed">{disclaimer}</p>
        </div>
      )}
    </div>
  );
};

export default VehicleRecallTile;
